/**
 * Browser-side API client.
 *
 * Adds the `X-CM-Request` header the CSRF middleware requires on every
 * mutation, sends cookies same-origin, and turns the server's error envelope
 * into a typed result instead of a thrown string. Screens branch on
 * `result.ok`, so a field-level validation response renders next to the field
 * rather than as a generic banner.
 */

export interface ApiFieldErrors {
  [field: string]: string;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: string; message: string; fields?: ApiFieldErrors; correlationId?: string };

async function request<T>(
  method: string, path: string, body?: unknown,
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        // Required by the CSRF middleware; a cross-origin request carrying
        // this header is preflighted and our CORS policy refuses it.
        "x-cm-request": "1",
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
  }

  if (res.ok) return { ok: true, data: payload as T };

  const err = (payload as { error?: Record<string, unknown> }).error;
  return {
    ok: false,
    code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
    message: typeof err?.["message"] === "string"
      ? (err["message"] as string)
      : "Something went wrong.",
    ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
    ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
  };
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  /** Partial update. The draft autosave sends only the fields that changed. */
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  /**
   * Raw bytes, same-origin. Cover photos cannot PUT to the R2 URL from the
   * browser: CSP connect-src is 'self', and the quarantine bucket is not
   * a same-origin host.
   */
  putBytes: <T>(path: string, body: Blob, mime: string) => putBytes<T>(path, body, mime),
  /** Multipart form. Do not set content-type - the boundary must come from the browser. */
  postForm: <T>(path: string, body: FormData) => postForm<T>(path, body),
};

async function putBytes<T>(path: string, body: Blob, mime: string): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "content-type": mime,
        "x-cm-request": "1",
      },
      body,
    });
  } catch {
    return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
  }

  if (res.ok) return { ok: true, data: payload as T };

  const err = (payload as { error?: Record<string, unknown> }).error;
  return {
    ok: false,
    code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
    message: typeof err?.["message"] === "string"
      ? (err["message"] as string)
      : "Something went wrong.",
    ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
    ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
  };
}

async function postForm<T>(path: string, body: FormData): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-cm-request": "1" },
      body,
    });
  } catch {
    return { ok: false, code: "NETWORK", message: "Could not reach the server. Check your connection." };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    return { ok: false, code: "BAD_RESPONSE", message: "The server sent an unexpected response." };
  }

  if (res.ok) return { ok: true, data: payload as T };

  const err = (payload as { error?: Record<string, unknown> }).error;
  return {
    ok: false,
    code: typeof err?.["code"] === "string" ? (err["code"] as string) : "INTERNAL",
    message: typeof err?.["message"] === "string"
      ? (err["message"] as string)
      : "Something went wrong.",
    ...(err?.["fields"] ? { fields: err["fields"] as ApiFieldErrors } : {}),
    ...(err?.["correlationId"] ? { correlationId: err["correlationId"] as string } : {}),
  };
}

export interface SessionResponse {
  authenticated: boolean;
  audience: string;
  signIn?: { method: string };
  user?: {
    id: string;
    email: string;
    displayName: string | null;
    status: string;
    emailVerified: boolean;
    createdAt?: string | null;
    avatarHref?: string | null;
  };
}

export const getSession = () => api.get<SessionResponse>("/auth/session");
